import React, { FC, useEffect, useRef, useState } from 'react';

import { useOutsideClick } from 'raa-react';

import { TextButton } from 'components/sharedElements/sharedElements';

import classNames from 'classnames';

interface ActionDropDownItem {
  text: string;
  onSelect: () => void;
  isLoading?: boolean;
}

interface ActionDropdownProps {
  icon: string;
  title: string;
  headerTitle?: string;
  items: Array<ActionDropDownItem>;
  xAlignment?: 'right' | 'left';
}

const ActionDropdown: FC<ActionDropdownProps> = ({ icon, items, title, headerTitle, xAlignment = 'left' }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [showItems, setShowItems] = useState<boolean>(false);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [isOutsideClick] = useOutsideClick(wrapperRef);

  const buttonHandler = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();
    event?.preventDefault();

    setShowItems(!showItems);
    if (!showItems) {
      wrapperRef.current?.focus();
    }
  };

  useEffect(() => {
    if (isOutsideClick) {
      setShowItems(false);
    }
  }, [isOutsideClick, setShowItems]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLElement>): void => {
    if (!showItems) return;

    if (event.key === 'Escape' || event.key === 'Tab') {
      setShowItems(false);
      buttonRef.current && buttonRef.current.focus();
      return;
    }

    if (event.key === 'Enter') {
      if (hoverIndex != null && !items[hoverIndex].isLoading) {
        setShowItems(false);
        items[hoverIndex].onSelect();
      }

      return;
    }

    const listElements = document.querySelectorAll('.item-dropdown li');
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (hoverIndex === null) {
        setHoverIndex(0);
        const focusedElement = listElements[0] as HTMLLIElement;
        focusedElement?.focus();
        event.preventDefault();
        return;
      }

      const newIndex: number = event.key == 'ArrowDown' ? hoverIndex + 1 : hoverIndex - 1;
      if (newIndex < items.length && newIndex >= 0) {
        setHoverIndex(newIndex);
        const focusedElement = listElements[newIndex] as HTMLLIElement;
        focusedElement?.focus();
      } else if (newIndex >= items.length) {
        setHoverIndex(0);
        const focusedElement = listElements[0] as HTMLLIElement;
        focusedElement?.focus();
      } else if (newIndex < 0) {
        setHoverIndex(items.length - 1);
        const focusedElement = listElements[items.length - 1] as HTMLLIElement;
        focusedElement?.focus();
      }
      event.preventDefault();

      return;
    }
  };

  return (
    <span onKeyDown={handleKeyPress} ref={wrapperRef}>
      <TextButton
        extraAttributes={{
          'aria-controls': title + '-dropdown',
          'aria-haspopup': true,
          'aria-expanded': showItems,
          ref: buttonRef
        }}
        icon={icon}
        onClick={buttonHandler}
      >
        {title}
      </TextButton>

      {showItems && (
        <div className="z-index-1 tw-relative tw-outline-none">
          <div
            id={title + '-dropdown'}
            aria-label="lista"
            className={classNames(
              'tw-absolute tw-z-50 tw-rounded-b-sm tw-border tw-border-raa-gray-3 tw-bg-raa-white tw-shadow-[5px_5px_10px_-3px_rgba(218,218,218.1)]',
              {
                'tw-right-0': xAlignment === 'right',
                'tw-left-0': xAlignment === 'left'
              }
            )}
          >
            {headerTitle && (
              <div className="tw-whitespace-nowrap tw-p-3 tw-text-start">
                {headerTitle}
                <hr className="tw-mt-3" />
              </div>
            )}

            <ul role="listbox" className="item-dropdown" data-testid="action-dropdown">
              {items.map((item, index) => {
                return (
                  <li
                    role="option"
                    aria-controls="option"
                    tabIndex={-1}
                    onMouseEnter={() => setHoverIndex(null)}
                    onClick={() => {
                      if (!item.isLoading) {
                        setShowItems(false);
                        item.onSelect();
                      }
                    }}
                    className={classNames(
                      'tw-cursor-pointer tw-whitespace-nowrap tw-p-3 tw-text-start tw-outline-none hover:tw-bg-raa-gray-2',
                      {
                        'tw-bg-raa-gray-2': index === hoverIndex
                      }
                    )}
                    key={index}
                    aria-selected={index === hoverIndex}
                  >
                    {item.text}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </span>
  );
};

export default ActionDropdown;
